import React from "react";
import { toast } from "react-toastify";

import { LOGIN_UNIFICADO } from "../../auth/routes";
import { formatCpf } from "../utils/format";

/**
 * O endereço que o DP entrega ao colaborador.
 *
 * São dois: o EXIBIDO é curto de ditar no balcão, e o COPIADO carrega o
 * `?perfil=colaborador` para que o link colado já abra a tela de login no perfil
 * certo. O `/colaborador/entrar` antigo continua funcionando como
 * redirecionamento, então bilhetes já entregues não quebram.
 */
export const portalUrlExibida = () => `${window.location.origin}${LOGIN_UNIFICADO()}`;
export const portalUrlCopiada = () => `${portalUrlExibida()}?perfil=colaborador`;

type Props = {
  password: string;
  cpf?: string | null;
  onClose: () => void;
};

/**
 * Senha provisória exibida UMA vez — o backend a devolve só na criação/reemissão
 * e depois guarda apenas o hash. Usado na aba "Acesso ao app" e na etapa
 * "Criar acesso ao app" do checklist de admissão.
 */
export default function ProvisionalPasswordModal({ password, cpf, onClose }: Props) {
  return (
    <div className="dpModalOverlay">
      <div className="dpModal">
        <header className="dpModal__header">
          <h2 className="dpModal__title">Senha provisória</h2>
          <button className="dpModal__close" onClick={onClose}>
            &times;
          </button>
        </header>

        <div className="dpModal__body">
          <p className="dpNotice">
            Anote ou copie agora: <strong>esta senha não será mostrada de novo</strong>. Se ela se
            perder, o caminho é reemitir outra na aba "Acesso ao app" da ficha do colaborador.
          </p>

          {/*
            Os TRÊS dados que o colaborador precisa, juntos: sem o endereço e
            sem o usuário, a senha sozinha não faz ninguém entrar.
          */}
          <div className="dpDefinitionGrid" style={{ marginBottom: 12 }}>
            <div>
              <div className="dpDefinition__label">Endereço do portal</div>
              <div className="dpDefinition__value">
                <span style={{ wordBreak: "break-all" }}>{portalUrlExibida()}</span>
              </div>
            </div>
            <div>
              <div className="dpDefinition__label">Usuário (é o CPF)</div>
              <div className="dpDefinition__value">{formatCpf(cpf)}</div>
            </div>
          </div>

          <div className="dpDefinition__label">Senha provisória</div>
          <div
            style={{
              padding: "16px",
              borderRadius: "10px",
              background: "var(--eco-ground)",
              fontFamily: "monospace",
              fontSize: "22px",
              letterSpacing: "2px",
              textAlign: "center",
              // Senha longa em tela estreita quebra em vez de vazar do modal.
              wordBreak: "break-all",
            }}
          >
            {password}
          </div>

          <p className="dpFormHint" style={{ marginTop: 12 }}>
            Entregue estes dados ao colaborador pessoalmente. No primeiro login o app vai obrigá-lo
            a trocar a senha.
          </p>
        </div>

        <footer className="dpModal__footer">
          <button
            type="button"
            className="dpBtn dpBtn--secondary"
            onClick={() => {
              // Copia o conjunto, não só a senha: é o que o colaborador
              // precisa receber para conseguir entrar.
              const texto = [
                `Portal: ${portalUrlCopiada()}`,
                `Usuário (CPF): ${formatCpf(cpf)}`,
                `Senha provisória: ${password}`,
                "No primeiro acesso o app vai pedir para você criar uma senha nova.",
              ].join("\n");

              // `clipboard` não existe em contexto não seguro (http sem
              // localhost); nesse caso os valores continuam visíveis na tela.
              navigator.clipboard
                ?.writeText(texto)
                .then(() => toast.success("Dados de acesso copiados."))
                .catch(() => toast.info("Copie os dados manualmente."));
            }}
          >
            Copiar dados de acesso
          </button>
          <button type="button" className="dpBtn dpBtn--primary" onClick={onClose}>
            Já anotei
          </button>
        </footer>
      </div>
    </div>
  );
}
